/**
 * client/src/lib/format-timestamp.ts
 *
 * Rule 13: backend stores UTC; UI renders viewer's local timezone.
 * Single utility — never call toLocaleString() directly elsewhere.
 */

export function safeParseDate(input: string | number | null | undefined): Date | null {
  if (input === null || input === undefined || input === "") return null;
  const d = new Date(input);
  return isNaN(d.getTime()) ? null : d;
}

type FormatVariant = "full" | "full_with_utc" | "compact" | "compact_with_tz" | "long";

// Construct the Intl formatters once at module load. `Intl.DateTimeFormat`
// construction (locale + timezone resolution) is the expensive part; the audit
// table re-formats one timestamp per row on every render, so per-call
// construction is wasteful. The instances are immutable and reusable.
const FULL_FMT = new Intl.DateTimeFormat("en-AU", {
  day: "numeric", month: "short", year: "numeric",
  hour: "numeric", minute: "2-digit", hour12: true,
  timeZoneName: "short",
});
// "long": spelled-out month + time, e.g. "17 August 2026 at 3:05 pm" — the vault
// row "Added" stamp, where a numeric 8/17 vs 17/8 read is ambiguous.
const LONG_FMT = new Intl.DateTimeFormat("en-AU", {
  day: "numeric", month: "long", year: "numeric",
  hour: "numeric", minute: "2-digit", hour12: true,
});
const COMPACT_FMT = new Intl.DateTimeFormat("en-AU", {
  day: "numeric", month: "short",
  hour: "numeric", minute: "2-digit", hour12: true,
});

export function formatTimestamp(utcDate: Date, opts: { format?: FormatVariant } = {}): string {
  if (isNaN(utcDate.getTime())) return "Unknown";
  const variant = opts.format ?? "full";
  if (variant === "full") return FULL_FMT.format(utcDate);
  if (variant === "long") return LONG_FMT.format(utcDate);
  if (variant === "full_with_utc") {
    const uh = utcDate.getUTCHours().toString().padStart(2, "0");
    const um = utcDate.getUTCMinutes().toString().padStart(2, "0");
    return `${FULL_FMT.format(utcDate)} (${uh}:${um} UTC)`;
  }
  // compact (no year)
  const compact = COMPACT_FMT.format(utcDate);
  // compact_with_tz: compact + short timezone name (e.g. "27 May, 7:33 am AEST").
  // Keeps the year off (unlike `full`) while anchoring the time to a zone for
  // audit reviewers — see Rule 13 (UTC storage, local display). The short TZ
  // token is lifted from FULL_FMT's parts, so no extra formatter is needed.
  if (variant === "compact_with_tz") {
    const tz = FULL_FMT.formatToParts(utcDate)
      .find((p) => p.type === "timeZoneName")?.value ?? "";
    return tz ? `${compact} ${tz}` : compact;
  }
  return compact;
}

export function formatDateOnly(utcDate: Date): string {
  if (isNaN(utcDate.getTime())) return "Unknown";
  return new Intl.DateTimeFormat("en-AU", {
    day: "numeric", month: "short", year: "numeric",
  }).format(utcDate);
}

export function formatCompactWithTooltip(utcDate: Date): { label: string; tooltip: string } {
  return {
    label: formatTimestamp(utcDate, { format: "compact_with_tz" }),
    tooltip: formatTimestamp(utcDate, { format: "full_with_utc" }),
  };
}

/**
 * Relative-time label. Default output is the compact "2d ago" form used
 * across the app (notifications, timelines, etc.) — UNCHANGED from before
 * this option existed, verified against `tests/unit/format-timestamp*.test.*`.
 *
 * `{ spelled: true }` selects the day-granularity spelled-out form ("2 days
 * ago", "Today") instead — the ONE other relative-time convention this
 * project uses, on the connection card's "Recent changes" feed, whose
 * accepted fixture and anti-fraud copy read "2 days ago" verbatim. This is
 * a mode on the one shared formatter, not a second permanent export — two
 * relative-time conventions rendered on the SAME screen (collapsed card
 * header vs. expanded "Recent changes") would themselves be a defect, and
 * a second copy of the same day-diff arithmetic + NaN guard would be
 * exactly the duplication the Reuse-First / Centralized Modules rule
 * exists to prevent.
 */
export function formatRelativeTime(
  utcDate: Date,
  opts: { spelled?: boolean } = {},
): string {
  if (isNaN(utcDate.getTime())) return "Unknown";
  const diffMs = Date.now() - utcDate.getTime();
  const m = Math.floor(diffMs / 60_000);
  const h = Math.floor(diffMs / 3_600_000);
  const d = Math.floor(diffMs / 86_400_000);
  if (opts.spelled) {
    if (d < 1) return "Today";
    if (d === 1) return "1 day ago";
    return `${d} days ago`;
  }
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d < 30) return `${d}d ago`;
  if (d < 365) return `${Math.floor(d / 30)}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}

/**
 * Format remaining time from now until a future deadline.
 * Used for countdown displays (e.g., "dispute window closes in 23h 14m").
 * Returns empty string if deadline has passed.
 */
export function formatRemainingTime(deadlineMs: number): string {
  const remaining = deadlineMs - Date.now();
  if (remaining <= 0) return "";

  const hours = Math.floor(remaining / (60 * 60 * 1000));
  const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
  }
  return "less than a minute";
}
